import { Select, Typography } from 'antd';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PhlebotomistOption } from '../../../types/phlebotomist-dashboard';

const { Text } = Typography;

interface IPhlebotomistSelectProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  phlebotomists: PhlebotomistOption[];
  regionIds?: string[]; // Filter phlebotomists by selected regions
  loading?: boolean;
  placeholder?: string;
}

export const PhlebotomistSelect: FC<IPhlebotomistSelectProps> = ({
  value,
  onChange,
  phlebotomists,
  regionIds = [],
  loading = false,
  placeholder,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  // Filter phlebotomists by region if regions are selected
  const filteredPhlebotomists = useMemo(() => {
    if (regionIds.length === 0) {
      return phlebotomists;
    }
    return phlebotomists.filter(
      (ph) => ph.regionId && regionIds.includes(ph.regionId)
    );
  }, [phlebotomists, regionIds]);

  const options = useMemo(() => {
    const phlebotomistOptions = filteredPhlebotomists.map((ph) => {
      const officeText = ph.assignedOfficeName || 'No office';
      const regionText = ph.regionName || 'No region';

      return {
        value: ph.id,
        // searchLabel is used for filtering
        searchLabel: `${ph.name} ${regionText} ${officeText}`,
        label: (
          <span>
            {ph.name} <Text type="secondary">({regionText})</Text>{' '}
            <Text type="secondary" style={{ fontSize: 12 }}>
              [{officeText}]
            </Text>
          </span>
        ),
      };
    });

    // Add "All" option at the top
    return [
      {
        value: '__ALL__',
        searchLabel: t('filters.allPhlebotomists', 'All Phlebotomists'),
        label: t('filters.allPhlebotomists', 'All Phlebotomists'),
      },
      ...phlebotomistOptions,
    ];
  }, [filteredPhlebotomists, t]);

  const handleChange = (selectedValue: string | undefined) => {
    // Convert "__ALL__" to undefined to represent "all phlebotomists"
    onChange(selectedValue === '__ALL__' ? undefined : selectedValue);
  };

  // Display value: show "__ALL__" when value is undefined
  const displayValue = value === undefined ? '__ALL__' : value;

  return (
    <Select
      value={displayValue}
      onChange={handleChange}
      options={options}
      loading={loading}
      style={{ width: '100%', minWidth: 0, maxWidth: 500 }}
      placeholder={
        placeholder || t('filters.selectPhlebotomist', 'Select Phlebotomist')
      }
      showSearch
      filterOption={(input, option) =>
        (option?.searchLabel as string)
          ?.toLowerCase()
          .includes(input.toLowerCase()) ?? false
      }
      optionLabelProp="label"
    />
  );
};

export default PhlebotomistSelect;
